"use strict";
(function () {
  if (!customElements.get("custom-modal")) {
    class CustomModal extends HTMLElement {
      // Bind methods in constructor to avoid multiple bind calls
      constructor() {
        super();
        this.dialog = null;
        this.openers = [];
        this.closeBtns = [];

        // Bind handlers once
        this.handleOpen = this.handleOpen.bind(this);
        this.handleKeydown = this.handleKeydown.bind(this);
        this.handleClose = this.handleClose.bind(this);
        this.handleBackdropClick = this.handleBackdropClick.bind(this);
      }

      connectedCallback() {
        this.setupElements();
        this.attachListeners();
      }

      disconnectedCallback() {
        this.detachListeners();
      }

      setupElements() {
        const { id } = this.dataset;

        this.dialog = this.querySelector('[data-component="dialog"]');

        if (id) {
          this.openers = Array.from(
            document.querySelectorAll(
              `[data-component="opener"][data-id="${id}"]`,
            ),
          );
        }

        this.closeBtns = Array.from(
          this.querySelectorAll('[data-component="close"]'),
        );
      }

      attachListeners() {
        // Attach opener listeners
        this.openers.forEach((opener) => {
          opener.addEventListener("click", this.handleOpen);
          opener.addEventListener("keydown", this.handleKeydown);
        });

        // Attach close button listeners
        this.closeBtns.forEach((btn) => {
          btn.addEventListener("click", this.handleClose);
        });

        // Attach dialog listeners
        if (this.dialog) {
          this.dialog.addEventListener("click", this.handleBackdropClick);
          this.dialog.addEventListener("cancel", this.handleClose);
        }
      }

      detachListeners() {
        // Detach opener listeners
        this.openers.forEach((opener) => {
          opener.removeEventListener("click", this.handleOpen);
          opener.removeEventListener("keydown", this.handleKeydown);
        });

        // Detach close button listeners
        this.closeBtns.forEach((btn) => {
          btn.removeEventListener("click", this.handleClose);
        });

        // Detach dialog listeners
        if (this.dialog) {
          this.dialog.removeEventListener("click", this.handleBackdropClick);
          this.dialog.removeEventListener("cancel", this.handleClose);
        }
      }

      handleOpen(e) {
        e.preventDefault();

        if (!this.dialog) return;

        this.dialog.showModal();
        this.setBodyOverflow("hidden");
      }

      handleKeydown(e) {
        const OPEN_KEYS = ["Enter", " "];

        if (OPEN_KEYS.includes(e.key)) {
          e.preventDefault();
          this.handleOpen(e);
        }
      }

      handleClose(e) {
        if (e) {
          e.preventDefault();
        }

        if (!this.dialog) return;

        this.dialog.close();
        this.setBodyOverflow("");
      }

      handleBackdropClick(e) {
        // Close only if clicking on the dialog backdrop (not its children)
        if (e.target === this.dialog) {
          this.handleClose();
        }
      }

      setBodyOverflow(value) {
        document.body.style.overflow = value;
      }

      // Public API methods for external control
      open() {
        this.handleOpen({ preventDefault: () => {} });
      }

      close() {
        this.handleClose();
      }

      isOpen() {
        return this.dialog?.open ?? false;
      }
    }

    customElements.define("custom-modal", CustomModal);
  }

  if (!customElements.get("custom-modal-opener")) {
    // I've renamed this class to avoid a naming conflict
    class CustomModalOpener extends HTMLElement {
      #modalId = this.dataset.id;
      #targetModal = null;

      constructor() {
        super();
        this.handleClick = this.handleClick.bind(this);
        this.handleKeydown = this.handleKeydown.bind(this);
      }

      connectedCallback() {
        if (!this.#modalId) {
          console.error(
            "CustomModalOpener: `data-id` attribute is missing.",
            this,
          );
          return;
        }

        // Find the modal this opener is supposed to control.
        this.#targetModal = document.querySelector(
          `custom-modal[data-id="${this.#modalId}"]`,
        );

        if (!this.#targetModal) {
          console.error(
            `CustomModalOpener: Could not find a <custom-modal> with data-id="${this.#modalId}".`,
          );
          return;
        }

        // Add accessibility attributes to make it behave like a button.
        this.setAttribute("role", "button");
        this.setAttribute("tabindex", "0");

        this.addEventListener("click", this.handleClick);
        this.addEventListener("keydown", this.handleKeydown);
      }

      disconnectedCallback() {
        this.removeEventListener("click", this.handleClick);
        this.removeEventListener("keydown", this.handleKeydown);
      }

      handleClick(event) {
        event.preventDefault();
        // Use the public .open() method on the target modal element.
        if (this.#targetModal) {
          this.#targetModal.open();
        }
      }

      handleKeydown(event) {
        // Trigger the click handler for Enter and Space keys for accessibility.
        if (event.key === "Enter" || event.key === " ") {
          this.handleClick(event);
        }
      }
    }
    customElements.define("custom-modal-opener", CustomModalOpener);
  }
})();

